import { useCallback, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { useGetLegacyInsuranceCarriersQuery } from '@/api/hooks/queries/use-get-legacy-insurance-carriers-query';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select.tsx';
import { insuranceStateOptions } from '@/lib/zipcode.ts';
import {
  type CarrierType,
  type InsuranceCarrier,
  type QueuedInsurance,
  SERVICED_CARRIER_TYPES,
  type UnprovidedInsurance,
  groupByType,
  prettyTypeName,
} from '@/types/insurance.type.ts';

const formId = 'insurance-input-form';
const formSchema = z.object({
  state: z.string().min(1, 'Please make a selection'),
  carrierType: z.enum([...SERVICED_CARRIER_TYPES], { message: 'Please make a selection' }),
  internalId: z.string().min(1, 'Please make a selection'),
  memberId: z.string().min(1, 'Member ID is required'),
});
type FormData = z.infer<typeof formSchema>;

interface StoredForm extends FormData {
  formType: CarrierType | null;
}

function blankForm(carrierType: CarrierType | null): StoredForm {
  return {
    formType: carrierType,
    state: '',
    carrierType: carrierType ?? ('' as CarrierType),
    internalId: '',
    memberId: '',
  };
}

function deepEquals(obj1: any, obj2: any): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

const useStore = create<{
  forms: StoredForm[];
  setForm: (type: CarrierType | null, data: FormData) => void;
  clearForm: (type: CarrierType | null) => void;
  getForm: (type: CarrierType | null) => FormData;
}>()(
  devtools(
    persist(
      (set, get) => ({
        forms: [],
        setForm: (type, data) => {
          set((state) => {
            const updated = { ...data, formType: type };
            const existing = state.forms.find((f) => f.formType === type);
            if (existing && deepEquals(existing, updated)) {
              return state; // no change
            }

            const newForms = state.forms.filter((f) => f.formType !== type);
            newForms.push(updated);
            return { forms: newForms };
          });
        },
        clearForm: (type) => {
          set((state) => ({
            forms: state.forms.filter((f) => f.formType !== type),
          }));
        },
        getForm: (type) => {
          const found = get().forms.find((f) => f.formType === type);
          return found ?? blankForm(type);
        },
      }),
      {
        name: 'InsuranceInputForm-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    { name: 'InsuranceInputForm-storage', enabled: import.meta.env.MODE !== 'test' }
  )
);

interface Props {
  initialData: Partial<FormData>;
  fixedCarrierType?: CarrierType;
  onSubmit: (insurance: QueuedInsurance) => void;
  onCannotProvide: (insurance: UnprovidedInsurance) => void;
}

export function InsuranceInputForm({
  initialData,
  fixedCarrierType,
  onSubmit,
  onCannotProvide,
}: Props) {
  const { getForm: getFormStore, setForm: setFormStore } = useStore();

  const storeKey = fixedCarrierType || null;
  const storedData = getFormStore(storeKey);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...storedData,
      ...initialData,
      ...(fixedCarrierType && { carrierType: fixedCarrierType }),
    },
  });
  useEffect(() => {
    if (fixedCarrierType) form.setValue('carrierType', fixedCarrierType);
    else form.resetField('carrierType');
    form.setValue('internalId', '');
    form.setValue('memberId', '');
  }, [form, fixedCarrierType]);

  const watchedFields = form.watch();
  const { state, carrierType, internalId, memberId } = watchedFields;

  useEffect(() => {
    setFormStore(storeKey, watchedFields);
  }, [watchedFields, storeKey, setFormStore]);

  const {
    data: carriers = [],
    isLoading,
    isSuccess,
    isError,
  } = useGetLegacyInsuranceCarriersQuery(state);
  const carriersByType = useMemo(() => groupByType(carriers), [carriers]);
  const servicedCarriersByType = useMemo(
    () =>
      new Map(
        [...carriersByType.keys()]
          .filter((type: CarrierType) => SERVICED_CARRIER_TYPES.has(type))
          .map((type: CarrierType) => [type, carriersByType.get(type)!])
      ),
    [carriersByType]
  );

  const carrierOptions = useMemo(() => {
    return fixedCarrierType
      ? (carriersByType.get(fixedCarrierType) ?? [])
      : (carriersByType.get(carrierType) ?? []);
  }, [fixedCarrierType, carriersByType, carrierType]);

  const carrierTypeNotReady = fixedCarrierType != null || !isSuccess || state === '';
  const plansNotReady = !isSuccess || state === '' || carrierType == null;

  const getSelectedCarrier = useCallback(
    (internalId: string) => {
      return carriers.find((c) => c.internalId === internalId);
    },
    [carriers]
  );

  const submitInsurance = useCallback(
    (data: FormData) => {
      const carrier = getSelectedCarrier(data.internalId);
      if (!carrier) throw new Error("Selected carrier that doesn't exist.");
      onSubmit({ carrier, memberId: data.memberId });
    },
    [getSelectedCarrier, onSubmit]
  );

  const cannotProvide = useCallback(() => {
    onCannotProvide({ carrierType, carrier: getSelectedCarrier(internalId), memberId });
  }, [carrierType, getSelectedCarrier, internalId, memberId, onCannotProvide]);

  return (
    <form
      onSubmit={form.handleSubmit(submitInsurance)}
      id={formId}
      className="grid grid-cols-1 gap-8 sm:gap-12"
    >
      <FieldGroup>
        <Controller
          name="state"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-state`}>State</FieldLabel>
              <NativeSelect
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e);
                  if (fixedCarrierType != null) {
                    form.setValue('carrierType', fixedCarrierType);
                  } else {
                    form.resetField('carrierType');
                  }
                  form.setValue('internalId', '');
                  form.setValue('memberId', '');
                }}
                id={`${formId}-state`}
                aria-invalid={fieldState.invalid}
              >
                <NativeSelectOption value="">Select...</NativeSelectOption>
                {insuranceStateOptions.map((option) => (
                  <NativeSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>

              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="carrierType"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-carrierType`}>Plan type</FieldLabel>
              <NativeSelect
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  form.setValue('internalId', '');
                  form.setValue('memberId', '');
                }}
                id={`${formId}-carrierType`}
                aria-invalid={fieldState.invalid}
                disabled={carrierTypeNotReady}
                aria-busy={isLoading}
                aria-describedby={isLoading ? `${formId}-carrierType-loading` : undefined}
              >
                <NativeSelectOption value={undefined}>
                  {isLoading ? 'Loading...' : 'Select...'}
                </NativeSelectOption>
                {[...servicedCarriersByType.keys()].map((carrierType) => (
                  <NativeSelectOption key={carrierType} value={carrierType}>
                    {prettyTypeName(carrierType)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              {isLoading && (
                <span id={`${formId}-carrierType-loading`} className="sr-only" aria-live="polite">
                  Loading plan types...
                </span>
              )}

              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="internalId"
          control={form.control}
          render={({ field, fieldState }) => {
            const selectedCarrier =
              carrierOptions.find((c) => c.internalId === field.value) ?? null;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${formId}-internalId`}>Plan name</FieldLabel>
                <Combobox
                  items={carrierOptions}
                  itemToStringLabel={(carrier) => carrier.displayName}
                  value={selectedCarrier}
                  onValueChange={(carrier) => {
                    field.onChange(carrier?.internalId ?? '');
                  }}
                  disabled={plansNotReady}
                >
                  <ComboboxInput
                    placeholder={isLoading ? 'Loading...' : 'Select...'}
                    id={`${formId}-internalId`}
                    aria-label="Plan name"
                    aria-invalid={fieldState.invalid}
                    aria-busy={isLoading}
                    aria-describedby={`${formId}-internalId`}
                    disabled={plansNotReady}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>No carriers found matching that keyword</ComboboxEmpty>
                    <ComboboxList>
                      {(carrier: InsuranceCarrier) => (
                        <ComboboxItem key={carrier.internalId} value={carrier}>
                          {carrier.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />

        <Controller
          name="memberId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-memberId`}>Member ID</FieldLabel>
              <Input
                {...field}
                placeholder="(e.g., AA11111A)"
                id={`${formId}-memberId`}
                aria-invalid={fieldState.invalid}
                autoComplete="off"
              />

              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      {isError && (
        <GeneralErrorIndicator>
          Failed to load insurance carriers. Please try again.
        </GeneralErrorIndicator>
      )}

      <div className="w-full space-y-8 justify-self-center sm:w-auto sm:space-y-4.5 sm:justify-self-start">
        <Button className="w-full sm:w-auto" type="submit">
          Continue
        </Button>

        <Button
          className="justify-self-center sm:justify-self-start sm:px-0"
          onClick={cannotProvide}
          size="sm"
          variant="link"
        >
          I don't have my insurance info
        </Button>
      </div>
    </form>
  );
}
